/**
 * Shared error describers for the EOS pages — maps the server's
 * `{ error: code }` envelopes (rocks/todos/issues routes) to copy the
 * UI can toast or render inline.
 */

export function eosErrorCode(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (typeof code === 'string') return code;
  }
  return null;
}

/** The server's human-readable `message` field, when present. */
export function eosErrorMessage(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { message?: unknown } } }).response;
    const message = response?.data?.message;
    if (typeof message === 'string') return message;
  }
  return null;
}

export function describeEosError(err: unknown, fallback: string): string {
  const code = eosErrorCode(err);
  switch (code) {
    case 'forbidden':
      return eosErrorMessage(err) ?? "You don't have permission for that.";
    case 'validation_failed':
      return 'Some fields look invalid — double-check and retry.';
    case 'invalid_quarter_start':
      return 'That quarter is not a valid quarter boundary — pick one from the dropdown.';
    case 'owner_not_found':
      return 'That owner is not an active member of this organization.';
    case 'department_not_found':
      return 'That department no longer exists.';
    case 'department_id_required':
      return 'Pick a department first.';
    case 'rock_not_found':
      return 'That rock no longer exists — refresh the page.';
    case 'milestone_not_found':
      return 'That milestone no longer exists — refresh the page.';
    case 'todo_not_found':
      return 'That to-do no longer exists — refresh the page.';
    case 'todo_completed':
      return 'Completed to-dos cannot be carried over.';
    case 'has_incomplete_subtasks':
      return 'Finish every subtask before completing this to-do.';
    case 'subtask_blocked':
      return 'An earlier subtask has to be done first (sequential mode).';
    case 'nested_subtasks_unsupported':
      return 'Subtasks can only be one level deep.';
    case 'issue_not_found':
      return 'That issue no longer exists — refresh the page.';
    case 'invalid_issue_ids':
      return 'The list changed while reordering — refresh and try again.';
    case 'already_promoted':
      return 'This issue was already promoted — refresh to see the link.';
    case 'meeting_not_found':
      return 'That meeting no longer exists — refresh the page.';
    case 'meeting_not_scheduled':
      return eosErrorMessage(err) ?? 'Only a scheduled meeting can be started.';
    case 'meeting_not_in_progress':
      return 'The meeting is not in progress.';
    case 'meeting_facilitator_only':
      return 'Only the facilitator can do that.';
    case 'unknown_segment':
      return eosErrorMessage(err) ?? 'That agenda segment does not exist.';
    case 'last_segment':
      return 'Already at the last segment — conclude the meeting instead.';
    case 'membership_not_found':
      return eosErrorMessage(err) ?? 'Every attendee must be an active member of this organization.';
    case 'not_an_attendee':
      return 'Only attendees of this meeting can rate it.';
    case 'series_not_found':
      return 'That meeting series no longer exists — refresh the page.';
    case 'invalid_agenda':
      return eosErrorMessage(err) ?? 'The agenda looks invalid — check the segment minutes.';
    case 'headline_not_found':
      return 'That headline no longer exists — refresh the page.';
    case 'invalid_vto_data':
      return 'The V/TO document looks invalid — check the section lengths and retry.';
    case 'version_not_found':
      return 'That V/TO version no longer exists — refresh the page.';
    case 'round_closed':
      return eosErrorMessage(err) ?? 'This round is closed to new scores.';
    case 'round_not_found':
      return 'That review round no longer exists — refresh the page.';
    case 'subject_not_found':
      return 'That person is not an active member of this organization.';
    case 'same_person':
      return eosErrorMessage(err) ?? 'Manager and report must be two different people.';
    case 'field_not_yours':
      return eosErrorMessage(err) ?? 'You can only edit your own side of this 1-on-1.';
    case 'one_on_one_not_found':
      return 'That 1-on-1 no longer exists — refresh the page.';
    case 'manager_not_found':
      return 'That manager is not an active member of this organization.';
    case 'report_not_found':
      return 'That report is not an active member of this organization.';
    default:
      return err instanceof Error ? err.message : fallback;
  }
}
